import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { Button } from '@/components/ui/button';
import { AlertCircle, ChevronRight, Download, Loader2 } from 'lucide-react';
import { useAdminConfig } from '@/hooks/useAdminConfig';
import { useToast } from '@/hooks/use-toast';

const AdminSchedulePage = () => {
  const { config } = useAdminConfig();
  const { toast } = useToast();
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [selectedServiceType, setSelectedServiceType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unscheduledJobs, setUnscheduledJobs] = useState<any[]>([]);
  const [conflicts, setConflicts] = useState<any[]>([]);
  const [todayVisits, setTodayVisits] = useState<number>(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.request('/admin/schedule/conflicts', 'GET');
        if (response.success && response.data) {
          setUnscheduledJobs(response.data.unscheduled || []);
          setConflicts(response.data.conflicts || []);
          const tv = response.data.today_visits ?? response.data.todayVisits ?? response.data.scheduled_today;
          if (tv != null) setTodayVisits(Number(tv));
        } else {
          setError('Failed to load schedule data');
        }
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Error loading schedule data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handler: Schedule job
  const handleScheduleJob = async (jobId: string, date: string) => {
    try {
      const response = await apiService.request(`/admin/jobs/${jobId}/schedule`, 'POST', {
        scheduledDate: date,
        scheduledBy: localStorage.getItem('adminId'),
        scheduledAt: new Date().toISOString()
      });
      if (response.success) {
        toast({ title: config.messages.jobScheduledSuccess });
        window.location.reload();
      } else {
        toast({ title: 'Error: ' + response.message });
      }
    } catch (error: any) {
      console.error('Schedule job error:', error);
      toast({ title: 'Failed to schedule job: ' + error.message });
    }
  };

  const urgentUnscheduled = unscheduledJobs.filter((j: any) => j.priority === 'URGENT');
  const metrics = [
    { label: "TODAY'S VISITS", value: String(todayVisits) },
    { label: 'UNSCHEDULED JOBS', value: String(unscheduledJobs.length), badge: urgentUnscheduled.length > 0 ? 'Requires Attention' : undefined, badgeColor: 'orange' },
    { label: 'PROVIDER CONFLICTS', value: String(conflicts.length), badgeColor: 'red' },
    { label: 'OVERDUE', value: String(unscheduledJobs.filter((j: any) => j.overdue).length), badgeColor: 'red' }
  ];

  // Format unscheduled jobs for display
  const needsSchedulingJobs = unscheduledJobs.map((job: any) => ({
    id: job.job_code || `#${job.job_id}`,
    status: job.priority === 'URGENT' ? 'URGENT' : 'NORMAL',
    title: job.service || 'Service',
    location: job.location || job.postcode || 'N/A'
  }));

  // Format schedule conflicts for display
  const scheduleConflicts = conflicts.map((conflict: any, idx: number) => ({
    title: conflict.conflict_type || 'Schedule Conflict',
    desc: conflict.description || '',
    action: idx === 0 ? 'View Conflict' : null
  }));

  const calendarDays = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
  const calendarDates = (() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const offset = firstDay === 0 ? 6 : firstDay - 1;
    const prevMonthDays = new Date(year, month, 0).getDate();
    const cells: { date: number; jobs: any[]; isPrev?: boolean }[] = [];
    for (let i = offset - 1; i >= 0; i--) {
      cells.push({ date: prevMonthDays - i, jobs: [], isPrev: true });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ date: d, jobs: [] });
    }
    while (cells.length < 35) cells.push({ date: cells.length - daysInMonth - offset + 1, jobs: [] });
    return cells;
  })();

  return (
    <>
      <Helmet>
        <title>Platform Schedule - Virewire</title>
      </Helmet>

      <AdminLayout>
        {loading ? (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading schedule data...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center max-w-md">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Schedule</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Button onClick={() => window.location.reload()} className="bg-teal-600 hover:bg-teal-700">
                Retry
              </Button>
            </div>
          </div>
        ) : (
        <div className="bg-gray-50 p-8 min-h-screen">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Platform Schedule</h1>
                <p className="text-gray-600">All scheduled service visits and operational calendar</p>
              </div>
              <div className="flex items-center gap-3">
                <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors">
                  <Download className="w-4 h-4" />
                  <span className="text-sm font-semibold">Export Calendar</span>
                </button>
              </div>
            </div>
          </div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            {metrics.map((metric, idx) => (
              <div key={idx} className="bg-white rounded-lg border border-gray-200 p-6">
                <p className="text-xs uppercase font-bold text-gray-600 mb-4">{metric.label}</p>
                <div className="flex items-end gap-3">
                  <p className="text-4xl font-bold text-gray-900">{metric.value}</p>
                  {metric.badge && (
                    <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-1 rounded-full mb-1">
                      {metric.badge}
                    </span>
                  )}
                  {metric.badgeColor === 'red' && !metric.badge && (
                    <div className="w-2 h-2 bg-red-600 rounded-full mb-2"></div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <div className="flex items-center gap-4 flex-wrap">
              <select 
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-600"
              >
                <option value="all">All Properties</option>
                <option value="prop1">Property 1</option>
              </select>

              <select 
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-600"
              >
                <option value="all">All Providers</option>
                <option value="prov1">Provider 1</option>
              </select>

              <select 
                value={selectedServiceType}
                onChange={(e) => setSelectedServiceType(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-600"
              >
                <option value="all">Service Type</option>
                <option value="maintenance">Maintenance</option>
              </select>

              <select 
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-600"
              >
                <option value="all">All Statuses</option>
                <option value="scheduled">Scheduled</option>
              </select>

              <select 
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-600"
              >
                <option value="all">Region/Postcode</option>
                <option value="london">London</option>
              </select>

              <Button className="bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm">
                Apply Filters
              </Button>
              <button className="text-gray-600 hover:text-gray-900 font-semibold text-sm">
                Clear
              </button>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-8">
            {/* Calendar */}
            <div className="col-span-3">
              <div className="bg-white rounded-lg border border-gray-200 p-8">
                {/* Days of week header */}
                <div className="grid grid-cols-7 gap-4 mb-6">
                  {calendarDays.map((day) => (
                    <div key={day} className="text-xs uppercase font-bold text-gray-600 text-center pb-2 border-b border-gray-200">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-4">
                  {calendarDates.map((item, idx) => (
                    <div
                      key={idx}
                      className="min-h-24 border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                    >
                      <p className={`text-sm font-bold mb-2 ${item.isPrev ? 'text-gray-400' : 'text-gray-900'}`}>
                        {item.date}
                      </p>
                      <div className="space-y-1">
                        {item.jobs.map((job, jobIdx) => (
                          <div
                            key={jobIdx}
                            className={`text-xs font-semibold px-2 py-1 rounded text-white text-center ${
                              job.color === 'blue'
                                ? 'bg-blue-500'
                                : job.color === 'red'
                                ? 'bg-red-500'
                                : job.color === 'green'
                                ? 'bg-green-500'
                                : 'bg-yellow-400'
                            }`}
                          >
                            {job.id}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="col-span-1 space-y-6">
              {/* Needs Scheduling */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-sm font-bold text-gray-900 mb-4">Needs Scheduling</h3>
                <div className="space-y-4">
                  {needsSchedulingJobs.map((job, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-bold text-gray-600">{job.id}</p>
                        <span className={`text-xs font-bold px-2 py-1 rounded ${
                          job.status === 'URGENT'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {job.status}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">{job.title}</p>
                      <p className="text-xs text-gray-600 mb-3">{job.location}</p>
                      <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold text-xs py-2">
                        Schedule
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Schedule Conflicts */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <h3 className="text-sm font-bold text-red-700">Schedule Conflicts</h3>
                </div>
                <div className="space-y-4">
                  {scheduleConflicts.map((conflict, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 border border-red-100">
                      <p className="text-sm font-semibold text-gray-900 mb-1">{conflict.title}</p>
                      <p className="text-xs text-gray-600 mb-2">{conflict.desc}</p>
                      {conflict.action && (
                        <button className="text-red-600 hover:text-red-700 font-semibold text-xs flex items-center gap-1">
                          {conflict.action} <ChevronRight className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
      </AdminLayout>
    </>
  );
};

export default AdminSchedulePage;
